// File: frontend/src/pages/staff/SalarySlip.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import api from '../../utils/axios';

const SalarySlip = () => {
  const { school } = useSchool();
  const { staffId } = useParams();
  const [searchParams] = useSearchParams();

  const month = searchParams.get('month') || new Date().getMonth() + 1;
  const year = searchParams.get('year') || new Date().getFullYear();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch complete salary slip data for target staff member
   */
  const fetchSalarySlip = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/api/v1/staff/payroll/${staffId}/slip`, {
        params: { month, year }
      });

      if (response.data?.success) {
        setData(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to load salary slip.');
      }
    } catch (err) {
      console.error('Error fetching salary slip:', err);
      setError(err.response?.data?.message || err.message || 'Error loading salary slip.');
    } finally {
      setLoading(false);
    }
  }, [staffId, month, year]);

  useEffect(() => {
    fetchSalarySlip();
  }, [fetchSalarySlip]);

  /**
   * Trigger print window
   */
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500 font-medium">Generating Salary Slip...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 p-8 flex flex-col items-center justify-center">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 text-center max-w-md w-full shadow-sm">
          <p className="text-red-600 font-bold mb-4">{error || 'Salary slip not found.'}</p>
          <Link to="/staff/payroll" className="px-4 py-2 bg-slate-800 text-white rounded-xl text-xs font-semibold">
            Return to Payroll
          </Link>
        </div>
      </div>
    );
  }

  const { staff, payroll } = data;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center py-8 px-4 print:p-0 print:bg-white">
      
      {/* Print CSS Rules */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 10mm;
          }
          body {
            background: white !important;
            color: black !important;
          }
          header, nav, .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>

      {/* Top Action Bar (Hidden on print) */}
      <div className="max-w-4xl w-full flex items-center justify-between mb-6 print:hidden">
        <Link
          to="/staff/payroll"
          className="bg-white text-slate-700 font-semibold px-4 py-2 rounded-xl text-sm border border-slate-200 shadow-sm hover:bg-slate-50 transition"
        >
          ← Back to Payroll
        </Link>
        <button
          onClick={handlePrint}
          className="bg-primary hover:bg-blue-600 text-white font-bold px-6 py-2.5 rounded-xl text-sm shadow-md transition flex items-center gap-2"
        >
          🖨️ Print Salary Slip
        </button>
      </div>

      {/* Printable A4 Salary Slip Container */}
      <div className="bg-white max-w-4xl w-full rounded-2xl border border-slate-200 p-8 md:p-12 shadow-lg print:shadow-none print:border-none print:max-w-none print:w-full print:p-6 print:rounded-none">
        
        {/* School Header */}
        <div className="text-center border-b-2 border-slate-800 pb-6 mb-6">
          {school?.logo ? (
            <img src={school.logo} alt="School Logo" className="w-16 h-16 object-contain mx-auto mb-3" />
          ) : (
            <div className="w-16 h-16 bg-primary text-white rounded-2xl mx-auto flex items-center justify-center font-extrabold text-2xl mb-3 shadow-md print:border">
              SMS
            </div>
          )}
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight uppercase">
            {school?.name || 'SCHOOL PORTAL'}
          </h1>
          <p className="text-xs text-slate-600 font-medium mt-1">
            {school?.address || 'Official Staff Confidential Salary & Payroll Slip'}
          </p>
        </div>

        {/* Slip Title & Period */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center mb-6 flex justify-between items-center px-6">
          <span className="font-mono font-bold text-xs text-slate-500">
            SLIP NO: PAY-{payroll.year}-{String(payroll.month).padStart(2, '0')}-{staff.id}
          </span>
          <h2 className="font-extrabold text-slate-800 text-sm uppercase tracking-wider">
            SALARY SLIP FOR MONTH {payroll.month} / {payroll.year}
          </h2>
          <span className="font-bold text-xs text-emerald-700 bg-emerald-100 border border-emerald-300 px-3 py-0.5 rounded-full capitalize">
            {payroll.status || 'Paid'}
          </span>
        </div>

        {/* Staff Information Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50/70 rounded-xl border border-slate-200 text-xs mb-8">
          <div>
            <span className="text-slate-400 font-semibold uppercase block">Employee Name</span>
            <span className="font-bold text-slate-900 text-sm">{staff.name}</span>
          </div>
          <div>
            <span className="text-slate-400 font-semibold uppercase block">Staff ID</span>
            <span className="font-mono font-bold text-primary text-sm">{staff.staff_id}</span>
          </div>
          <div>
            <span className="text-slate-400 font-semibold uppercase block">Designation</span>
            <span className="font-bold text-slate-900 text-sm">{staff.designation}</span>
          </div>
          <div>
            <span className="text-slate-400 font-semibold uppercase block">Department</span>
            <span className="font-bold text-slate-900 text-sm">{staff.department}</span>
          </div>
        </div>

        {/* Salary Breakdown Table */}
        <div className="overflow-x-auto mb-8">
          <table className="w-full text-left text-xs border border-slate-200 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-slate-800 text-white uppercase font-bold text-[11px]">
                <th className="py-3 px-4">Earnings / Description</th>
                <th className="py-3 px-4 text-right">Amount (Rs.)</th>
                <th className="py-3 px-4">Deductions / Description</th>
                <th className="py-3 px-4 text-right">Amount (Rs.)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
              <tr>
                <td className="py-3 px-4 font-semibold">Basic Salary</td>
                <td className="py-3 px-4 text-right font-mono font-bold">Rs. {parseFloat(payroll.basic_salary).toLocaleString(undefined, { minimumFractionDigits: 0 })}</td>
                <td className="py-3 px-4 text-slate-600">Tax & Provident Deductions</td>
                <td className="py-3 px-4 text-right font-mono text-red-600 font-semibold">-Rs. {parseFloat(payroll.deductions).toLocaleString(undefined, { minimumFractionDigits: 0 })}</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-semibold">Medical & Transport Allowances</td>
                <td className="py-3 px-4 text-right font-mono text-emerald-600 font-semibold">+Rs. {parseFloat(payroll.allowances).toLocaleString(undefined, { minimumFractionDigits: 0 })}</td>
                <td className="py-3 px-4 text-slate-400">-</td>
                <td className="py-3 px-4 text-right font-mono text-slate-400">Rs. 0</td>
              </tr>
              <tr className="bg-slate-50 font-bold">
                <td className="py-3 px-4 text-slate-900">Total Gross Earnings</td>
                <td className="py-3 px-4 text-right font-mono text-slate-900">
                  Rs. {(parseFloat(payroll.basic_salary) + parseFloat(payroll.allowances)).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                </td>
                <td className="py-3 px-4 text-slate-900">Total Deductions</td>
                <td className="py-3 px-4 text-right font-mono text-red-600">
                  -Rs. {parseFloat(payroll.deductions).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Net Take Home Summary Box */}
        <div className="p-6 bg-slate-900 text-white rounded-xl mb-12 flex items-center justify-between">
          <div>
            <span className="text-slate-400 text-xs uppercase block font-semibold">Net Payable Take-Home Salary</span>
            <span className="text-xs text-slate-300">Payment Mode: <span className="capitalize text-white font-semibold">{payroll.payment_method || 'Bank Transfer'}</span> • Date: {new Date(payroll.payment_date).toLocaleDateString()}</span>
          </div>
          <div className="text-right">
            <span className="font-mono text-2xl md:text-3xl font-black text-emerald-400">
              Rs. {parseFloat(payroll.net_salary).toLocaleString(undefined, { minimumFractionDigits: 0 })}
            </span>
          </div>
        </div>

        {/* Signature Blocks */}
        <div className="grid grid-cols-2 gap-8 pt-12 border-t border-slate-200">
          <div className="text-center">
            <div className="h-12 border-b border-slate-400 max-w-[200px] mx-auto mb-2"></div>
            <span className="text-xs font-bold text-slate-700 uppercase">Employee Signature</span>
          </div>
          <div className="text-center flex flex-col items-center">
            <div className="h-12 border-b border-slate-400 max-w-[200px] w-full mb-2 flex items-end justify-center gap-4">
              {school?.stamp && (
                <img src={school.stamp} alt="Stamp" className="h-10 object-contain opacity-80" />
              )}
              {school?.principal_signature && (
                <img src={school.principal_signature} alt="Signature" className="h-10 object-contain" />
              )}
            </div>
            <span className="text-xs font-bold text-slate-700 uppercase">
              {school?.principal_name || 'Principal'} Signature & Stamp
            </span>
          </div>
        </div>

      </div>

    </div>
  );
};

export default SalarySlip;
